import React, { useState } from "react";
import axios from "axios";
import {
    Stepper,
    Group,
    Button,
    Stack,
    Text,
    Alert,
    Paper,
    Divider,
    Select,
    TextInput,
    NumberInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";
import { useParams } from "react-router-dom";

type Draft = {
    country: string;
    scheduledDate: string;
    scheduledTime: string;
    pickupPlace: string;
    dropOffPlace: string;
    contactperson: string;
    contactpersonEmail: string;
    contactpersonMobile?: string;
    whatsappCountryCode: string;
    whatsappNumber: string;
    numberOfPersons: number;
    numberOfLuggage: number;
    note?: string;
    whatsappCountryIso2?: string;
};

const PRIORITY_ISO2 = [
    "DK",
    "CA",
    "CN",
    "FR",
    "DE",
    "HK",
    "IN",
    "ID",
    "JP",
    "MY",
    "MX",
    "SG",
    "ES",
    "TW",
    "TH",
    "TR",
    "GB",
    "VN",
    "BG",
];

const MIN_DATE = "2026-04-20";
const MAX_DATE = "2026-05-04";

function isWithinRange(dateStr: string) {
    if (!dateStr) return false;
    return dateStr >= MIN_DATE && dateStr <= MAX_DATE;
}

export default function TransportLocalRequestStepper() {
    const [active, setActive] = useState<number>(0);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { eventId } = useParams<{ eventId: string }>();

    const isMobile = useMediaQuery("(max-width: 768px)");

    const [draft, setDraft] = useState<Draft>({
        country: "",
        scheduledDate: "",
        scheduledTime: "",
        pickupPlace: "",
        dropOffPlace: "",
        contactperson: "",
        contactpersonEmail: "",
        contactpersonMobile: "",
        whatsappCountryCode: "",
        whatsappNumber: "",
        numberOfPersons: 1,
        numberOfLuggage: 0,
        note: "",
        whatsappCountryIso2: undefined,
    });

    const whatsappCountryOptions = React.useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES.filter((c) => prioritySet.has(c.iso2))
            .sort((a, b) => PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES.filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: "Popular", items: popularItems },
            { group: "All countries", items: restItems },
        ];
    }, []);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.iso2 === draft.whatsappCountryIso2)?.country ??
        (draft.whatsappCountryCode ? "Unknown" : null);

    const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

    const whatsappValid =
        !!draft.whatsappCountryCode &&
        !!draft.whatsappNumber &&
        draft.whatsappNumber.replace(/\s+/g, "").length >= 6;

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return (
                    draft.contactperson.trim().length > 0 &&
                    emailValid(draft.contactpersonEmail) &&
                    whatsappValid
                );
            case 1:
                return (
                    draft.country.trim().length > 0 &&
                    !!draft.scheduledDate &&
                    isWithinRange(draft.scheduledDate) &&
                    !!draft.scheduledTime
                );
            case 2:
                return (
                    draft.pickupPlace.trim().length > 0 &&
                    draft.dropOffPlace.trim().length > 0 &&
                    (draft.numberOfPersons ?? 0) > 0 &&
                    (draft.numberOfLuggage ?? 0) >= 0
                );
            case 3:
                return true;
            default:
                return true;
        }
    };

    const next = () => {
        if (active < 4 && isStepValid(active)) setActive((a) => a + 1);
    };

    const prev = () => setActive((a) => Math.max(0, a - 1));

    const submit = async () => {
        setError(null);

        if (!isStepValid(0) || !isStepValid(1) || !isStepValid(2)) {
            setError("Der mangler oplysninger – gå tilbage og udfyld alle felter.");
            return;
        }

        if (!eventId) {
            setError("EventId mangler i URL.");
            return;
        }

        if (!isWithinRange(draft.scheduledDate)) {
            setError(`Dato skal være mellem ${MIN_DATE} og ${MAX_DATE}.`);
            return;
        }

        const toTimeSpan = (t: string) => {
            if (!t) return null;
            return t.length === 5 ? `${t}:00` : t;
        };

        const payload = {
            Kind: 3, // Local
            EventId: Number(eventId),
            Country: draft.country,
            ScheduledDate: draft.scheduledDate,
            ScheduledTime: toTimeSpan(draft.scheduledTime),
            PickupPlace: draft.pickupPlace,
            DropOffPlace: draft.dropOffPlace,
            Description: `${draft.pickupPlace} -> ${draft.dropOffPlace}`,
            ContactPerson: draft.contactperson,
            ContactPersonEmail: draft.contactpersonEmail,
            ContactPersonMobile: draft.contactpersonMobile || null,
            NumberOfPersons: draft.numberOfPersons,
            NumberOfLuggage: draft.numberOfLuggage,
            Note: draft.note || null,
            WhatsAppCountryCode: draft.whatsappCountryCode,
            WhatsAppNumber: draft.whatsappNumber,
        };

        try {
            setSubmitLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL;

            console.log("Submitting local payload:", payload);
            console.log("eventId:", eventId);

            const res = await axios.post(
                `${apiUrl}/api/v2/transport-requests/event/${eventId}`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            showNotification({
                title: "Local transport request created",
                message: `Din lokale transport er registreret (ID: ${res.data?.transportRequestId ?? "OK"}).`,
            });

            setActive(4);
        } catch (e: any) {
            console.error("Submit failed:", e);
            console.error("Response status:", e?.response?.status);
            console.error("Response data:", e?.response?.data);

            const apiError =
                typeof e?.response?.data === "string"
                    ? e.response.data
                    : e?.response?.data?.message ||
                    e?.response?.data?.title ||
                    "Kunne ikke oprette lokal transport.";

            setError(apiError);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Stack>
            {error && (
                <Alert color="red" title="Fejl">
                    {typeof error === "string" ? error : "Fejl ved oprettelse."}
                </Alert>
            )}

            <Stepper
                active={active}
                allowNextStepsSelect={false}
                orientation={isMobile ? "vertical" : "horizontal"}
                size={isMobile ? "sm" : "md"}
            >
                <Stepper.Step label="Contact">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Contact details</Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Contact person"
                                value={draft.contactperson}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, contactperson: e.target.value }))
                                }
                                required
                            />

                            <TextInput
                                label="Email"
                                value={draft.contactpersonEmail}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, contactpersonEmail: e.target.value }))
                                }
                                required
                                type="email"
                            />

                            <TextInput
                                label="Mobile (optional)"
                                placeholder="e.g. +45 12 34 56 78"
                                value={draft.contactpersonMobile ?? ""}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, contactpersonMobile: e.target.value }))
                                }
                            />

                            <Divider my="xs" />

                            <Text fw={600}>WhatsApp number</Text>

                            <Select
                                label="WhatsApp country"
                                data={whatsappCountryOptions}
                                searchable
                                nothingFoundMessage="No matches"
                                comboboxProps={{ withinPortal: false }}
                                value={draft.whatsappCountryIso2 ?? null}
                                onChange={(iso2) => {
                                    const c = WHATSAPP_COUNTRIES.find((x) => x.iso2 === iso2);
                                    setDraft((d) => ({
                                        ...d,
                                        whatsappCountryIso2: iso2 ?? undefined,
                                        whatsappCountryCode: c?.code ?? "",
                                    }));
                                }}
                                required
                            />

                            {draft.whatsappCountryCode && (
                                <Text size="sm" c="dimmed">
                                    Selected country: <b>{selectedWhatsappCountry ?? "Unknown"}</b>
                                </Text>
                            )}

                            <TextInput
                                label="WhatsApp number"
                                placeholder="e.g. 12345678"
                                value={draft.whatsappNumber ?? ""}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const cleaned = raw.replace(/[^\d\s-]/g, "");
                                    setDraft((d) => ({ ...d, whatsappNumber: cleaned }));
                                }}
                                required
                            />

                            {!isStepValid(0) && (
                                <Text size="sm" c="red">
                                    Fill in your name, valid email and WhatsApp before you can continue.
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Transport">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Transport date and time</Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Country"
                                value={draft.country}
                                onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
                                required
                            />

                            <TextInput
                                label="Date"
                                type="date"
                                value={draft.scheduledDate}
                                min={MIN_DATE}
                                max={MAX_DATE}
                                onChange={(e) => {
                                    const v = e.target.value;

                                    if (!v) {
                                        setDraft((d) => ({ ...d, scheduledDate: "" }));
                                        return;
                                    }

                                    if (!isWithinRange(v)) {
                                        showNotification({
                                            title: "Invalid date",
                                            message: `Please choose a date between ${MIN_DATE} and ${MAX_DATE}.`,
                                        });
                                        return;
                                    }

                                    setDraft((d) => ({ ...d, scheduledDate: v }));
                                }}
                                required
                            />

                            <TextInput
                                label="Time"
                                type="time"
                                value={draft.scheduledTime}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, scheduledTime: e.target.value }))
                                }
                                required
                            />
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Route">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">Pickup and drop-off</Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Pickup place"
                                placeholder="Write pickup location"
                                value={draft.pickupPlace}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, pickupPlace: e.target.value }))
                                }
                                required
                            />

                            <TextInput
                                label="Drop-off place"
                                placeholder="Write drop-off location"
                                value={draft.dropOffPlace}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, dropOffPlace: e.target.value }))
                                }
                                required
                            />

                            <NumberInput
                                label="Number of persons"
                                value={draft.numberOfPersons}
                                min={1}
                                onChange={(v) => {
                                    if (typeof v === "number" && !Number.isNaN(v)) {
                                        setDraft((d) => ({ ...d, numberOfPersons: v }));
                                    }
                                }}
                                required
                            />

                            <NumberInput
                                label="Number of luggage"
                                value={draft.numberOfLuggage}
                                min={0}
                                onChange={(v) => {
                                    if (typeof v === "number" && !Number.isNaN(v)) {
                                        setDraft((d) => ({ ...d, numberOfLuggage: v }));
                                    }
                                }}
                                required
                            />

                            <TextInput
                                label="Note (optional)"
                                value={draft.note ?? ""}
                                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                            />
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Review">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Review</Text>
                        <Text size="sm" c="dimmed">Check the information before approving.</Text>

                        <Divider my="sm" />

                        <Stack gap={6}>
                            <Text><b>Contact:</b> {draft.contactperson} ({draft.contactpersonEmail})</Text>
                            {draft.contactpersonMobile?.trim() ? (
                                <Text><b>Mobile:</b> {draft.contactpersonMobile}</Text>
                            ) : null}
                            <Text><b>WhatsApp:</b> {draft.whatsappCountryCode} {draft.whatsappNumber}</Text>
                            <Text><b>Country:</b> {draft.country}</Text>
                            <Text><b>Date:</b> {draft.scheduledDate}</Text>
                            <Text><b>Time:</b> {draft.scheduledTime}</Text>
                            <Text><b>Pickup:</b> {draft.pickupPlace}</Text>
                            <Text><b>Drop-off:</b> {draft.dropOffPlace}</Text>
                            <Text><b>Persons:</b> {draft.numberOfPersons}</Text>
                            <Text><b>Luggage:</b> {draft.numberOfLuggage}</Text>
                            {draft.note?.trim() ? <Text><b>Note:</b> {draft.note}</Text> : null}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Completed>
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Confirmed ✅</Text>

                        <Text>
                            Thank you — your local transport request has been registered.
                        </Text>

                        <Text>
                            WhatsApp: {draft.whatsappCountryCode} {draft.whatsappNumber}
                        </Text>

                        <Text mt="sm">
                            If you need to change or cancel your request, please contact{" "}
                            <Text
                                component="a"
                                href="https://wa.me/4540158797"
                                target="_blank"
                                fw={600}
                                c="green"
                            >
                                +45 40 15 87 97
                            </Text>.
                        </Text>
                    </Paper>
                </Stepper.Completed>
            </Stepper>

            {active < 4 && (
                <Group justify="space-between" mt="md">
                    <Button variant="default" onClick={prev} disabled={active === 0}>
                        Back
                    </Button>

                    {active === 3 ? (
                        <Button onClick={submit} loading={submitLoading}>
                            Approve request
                        </Button>
                    ) : (
                        <Button onClick={next} disabled={!isStepValid(active)}>
                            Next
                        </Button>
                    )}
                </Group>
            )}
        </Stack>
    );
}